import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Rating,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Alert,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import LockIcon from '@mui/icons-material/Lock';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import ReplyIcon from '@mui/icons-material/Reply';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SyncIcon from '@mui/icons-material/Sync';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useAuth } from '../../context/authContext';
import useGoogleReview from '../../hooks/useGoogleReview';
import GoogleRatingMatchDialog from '../Entertainment/GoogleRatingMatchDialog';

const PAGE_SIZE = 25;

const STATUS_CONFIG = {
  new: { label: 'Neu', color: 'info', Icon: NewReleasesIcon },
  inProgress: { label: 'In Bearbeitung', color: 'warning', Icon: HourglassEmptyIcon },
  resolved: { label: 'Beantwortet', color: 'success', Icon: CheckCircleIcon },
};

// Icon-only status indicator used in compact list cards.
function StatusIcon({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.new;
  const Icon = cfg.Icon;
  if (!Icon) return null;
  return (
    <Tooltip title={cfg.label}>
      <Icon fontSize='small' color={cfg.color} />
    </Tooltip>
  );
}

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function StatusChip({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.new;
  const Icon = cfg.Icon;
  return (
    <Chip
      size='small'
      color={cfg.color}
      variant='outlined'
      icon={Icon ? <Icon fontSize='inherit' /> : undefined}
      label={cfg.label}
      sx={{
        maxWidth: '100%',
        '& .MuiChip-label': {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
      }}
    />
  );
}

function ReviewCard({ review, selected, onSelect }) {
  const hasReply = Boolean(review?.reply?.comment);
  const lockedByOther = !!review?.lockedByOtherUser;
  return (
    <Paper
      elevation={selected ? 4 : 1}
      onClick={() => onSelect(review)}
      sx={{
        p: 2,
        mb: 1,
        cursor: 'pointer',
        borderLeft: '4px solid',
        borderLeftColor:
          review.starRating >= 4
            ? 'success.main'
            : review.starRating === 3
              ? 'warning.main'
              : 'error.main',
        bgcolor: selected ? 'action.selected' : 'background.paper',
        '&:hover': { bgcolor: 'action.hover' },
        minWidth: 0,
      }}
    >
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        useFlexGap
        sx={{ mb: 0.25, gap: 0.5, flexWrap: 'wrap' }}
      >
        <Typography variant='caption' color='text.secondary' sx={{ minWidth: 0 }}>
          {formatDate(review.createTime)}
        </Typography>
        <Stack direction='row' spacing={0.5} alignItems='center' sx={{ flexShrink: 0 }}>
          {lockedByOther ? (
            <Tooltip
              title={`Wird gerade bearbeitet von ${review.lockedByUsername || 'einer/einem anderen Benutzer:in'}`}
            >
              <LockIcon fontSize='small' color='warning' />
            </Tooltip>
          ) : null}
          {hasReply ? (
            <Tooltip title='Antwort vorhanden'>
              <ReplyIcon fontSize='small' color='action' />
            </Tooltip>
          ) : null}
          <StatusIcon status={review.status} />
        </Stack>
      </Stack>
      <Rating value={review.starRating || 0} size='small' readOnly sx={{ mb: 0.5 }} />
      <Typography variant='subtitle2' noWrap sx={{ maxWidth: '100%', minWidth: 0 }}>
        {review.reviewerDisplayName || 'Anonym'}
        {review.branchName ? (
          <Typography
            variant='caption'
            color='text.secondary'
            component='span'
            sx={{ ml: 1 }}
          >
            · {review.branchName}
          </Typography>
        ) : null}
      </Typography>
      <Typography
        variant='body2'
        color='text.secondary'
        sx={{
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          mt: 0.5,
        }}
      >
        {review.comment || '(keine Beschreibung – nur Sternebewertung)'}
      </Typography>
    </Paper>
  );
}

function ReviewDetail({
  review,
  onStatusChange,
  onGenerateAi,
  onPostReply,
  onDeleteReply,
  onSaveNote,
  savingStatus,
  generatingAi,
  postingReply,
  deletingReply,
  savingNote,
  lockState,
}) {
  const replyRef = useRef(null);
  const noteRef = useRef(null);
  const [draftFromAi, setDraftFromAi] = useState(null);

  useEffect(() => {
    setDraftFromAi(null);
  }, [review?._id]);

  if (!review) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
        <Typography variant='body2'>
          Wähle links eine Rezension, um Details zu sehen und zu antworten.
        </Typography>
      </Paper>
    );
  }

  const initialReply = draftFromAi ?? review?.reply?.comment ?? '';
  const hasExistingReply = Boolean(review?.reply?.comment);
  const lockedByOther = !!lockState?.lockedByOtherUser;
  const lockOwnerLabel = lockState?.lockedByUsername || 'einer/einem anderen Benutzer:in';

  const handleUseAiSuggestion = () => {
    if (review?.aiSuggestion?.responseText) {
      setDraftFromAi(review.aiSuggestion.responseText);
      if (replyRef.current) {
        replyRef.current.value = review.aiSuggestion.responseText;
        replyRef.current.focus();
      }
    }
  };

  const handlePost = () => {
    const value = replyRef.current?.value?.trim() || '';
    if (!value) {
      toast.error('Antworttext darf nicht leer sein');
      return;
    }
    onPostReply(value);
  };

  const handleGenerate = async () => {
    const result = await onGenerateAi();
    if (result?.aiSuggestion?.responseText) {
      setDraftFromAi(result.aiSuggestion.responseText);
      if (replyRef.current) {
        replyRef.current.value = result.aiSuggestion.responseText;
      }
    }
  };

  const handleSaveNote = () => {
    const value = noteRef.current?.value ?? '';
    onSaveNote(value);
  };

  return (
    <Paper sx={{ p: { xs: 2, md: 3 }, maxWidth: '100%', overflow: 'hidden' }}>
      <Stack spacing={2}>
        {lockedByOther ? (
          <Alert severity='warning' icon={<LockIcon fontSize='inherit' />}>
            Diese Rezension wird gerade von <strong>{lockOwnerLabel}</strong> bearbeitet. Änderungen sind solange gesperrt.
          </Alert>
        ) : null}
        <Box>
          <Stack
            direction='row'
            spacing={1}
            alignItems='center'
            useFlexGap
            sx={{ mb: 0.5, flexWrap: 'wrap' }}
          >
            <Rating value={review.starRating || 0} readOnly />
            <Box sx={{ flex: '1 1 auto', minWidth: 8 }} />
            <StatusChip status={review.status} />
          </Stack>
          <Stack
            direction='row'
            spacing={1}
            alignItems='baseline'
            useFlexGap
            sx={{ mb: 0.5, flexWrap: 'wrap' }}
          >
            <Typography variant='subtitle2' sx={{ minWidth: 0, wordBreak: 'break-word' }}>
              {review.reviewerDisplayName || 'Anonym'}
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              · {formatDate(review.createTime)}
            </Typography>
          </Stack>
          <Typography variant='caption' color='text.secondary' sx={{ wordBreak: 'break-word' }}>
            Standort: {review.locationTitle || review.googleLocationName}
            {review.entertainmentName ? ` · ${review.entertainmentName}` : ''}
            {review.branchName ? ` · ${review.branchName}` : ''}
          </Typography>
        </Box>

        <Divider />

        <Box>
          <Typography variant='subtitle2' gutterBottom>
            Kommentar
          </Typography>
          <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
            {review.comment || '(keine Beschreibung – nur Sternebewertung)'}
          </Typography>
        </Box>

        <Divider />

        <Box>
          <Stack
            direction='row'
            alignItems='center'
            spacing={1}
            useFlexGap
            sx={{ mb: 1, flexWrap: 'wrap' }}
          >
            <Typography variant='subtitle2' sx={{ flex: '1 1 180px' }}>
              Öffentliche Antwort
            </Typography>
            <Tooltip title='KI-Antwortvorschlag generieren'>
              <span>
                <Button
                  size='small'
                  startIcon={
                    generatingAi ? <CircularProgress size={14} /> : <AutoAwesomeIcon />
                  }
                  onClick={handleGenerate}
                  disabled={generatingAi || lockedByOther}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  KI-Vorschlag
                </Button>
              </span>
            </Tooltip>
            {review?.aiSuggestion?.responseText ? (
              <Button size='small' onClick={handleUseAiSuggestion} disabled={lockedByOther}>
                Übernehmen
              </Button>
            ) : null}
          </Stack>

          {review?.aiSuggestion?.responseText ? (
            <Alert severity='info' sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
              <Typography variant='caption' color='text.secondary'>
                KI-Entwurf ({review.aiSuggestion.tone || 'neutral'}, erzeugt{' '}
                {formatDate(review.aiSuggestion.generatedAt)}):
              </Typography>
              <Typography variant='body2' sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                {review.aiSuggestion.responseText}
              </Typography>
            </Alert>
          ) : null}

          <TextField
            inputRef={replyRef}
            key={`reply-${review._id}-${draftFromAi ? 'ai' : hasExistingReply ? 'existing' : 'empty'}`}
            defaultValue={initialReply}
            multiline
            minRows={5}
            fullWidth
            disabled={lockedByOther}
            placeholder='Antworte dem Gast – wird öffentlich auf Google sichtbar'
            inputProps={{ maxLength: 4096 }}
          />

          <Stack
            direction='row'
            spacing={1}
            useFlexGap
            sx={{ mt: 1, flexWrap: 'wrap' }}
            justifyContent='flex-end'
          >
            {hasExistingReply ? (
              <Button
                color='error'
                size='small'
                startIcon={
                  deletingReply ? <CircularProgress size={14} /> : <DeleteOutlineIcon />
                }
                onClick={onDeleteReply}
                disabled={deletingReply || lockedByOther}
              >
                Antwort entfernen
              </Button>
            ) : null}
            <Button
              variant='contained'
              size='small'
              startIcon={postingReply ? <CircularProgress size={14} /> : <ReplyIcon />}
              onClick={handlePost}
              disabled={postingReply || lockedByOther}
            >
              {hasExistingReply ? 'Antwort aktualisieren' : 'Antwort veröffentlichen'}
            </Button>
          </Stack>
        </Box>

        <Divider />

        <Box>
          <Typography variant='subtitle2' gutterBottom>
            Status & interne Notiz
          </Typography>
          <Stack direction='row' spacing={1} useFlexGap sx={{ mb: 1, flexWrap: 'wrap' }}>
            {['new', 'inProgress', 'resolved'].map(s => (
              <Button
                key={s}
                size='small'
                variant={review.status === s ? 'contained' : 'outlined'}
                color={STATUS_CONFIG[s].color}
                onClick={() => onStatusChange(s)}
                disabled={savingStatus || review.status === s || lockedByOther}
              >
                {STATUS_CONFIG[s].label}
              </Button>
            ))}
          </Stack>
          <TextField
            inputRef={noteRef}
            key={`note-${review._id}`}
            defaultValue={review.internalNote || ''}
            multiline
            minRows={2}
            fullWidth
            disabled={lockedByOther}
            placeholder='Interne Notiz (nicht bei Google sichtbar)'
            inputProps={{ maxLength: 4000 }}
          />
          <Stack direction='row' justifyContent='flex-end' sx={{ mt: 1, flexWrap: 'wrap' }}>
            <Button size='small' onClick={handleSaveNote} disabled={savingNote || lockedByOther}>
              {savingNote ? <CircularProgress size={14} /> : 'Notiz speichern'}
            </Button>
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
}

export default function GoogleReviewsView() {
  const authCtx = useAuth();
  const canRead = authCtx?.checkRight ? authCtx.checkRight('googleReview:read') : false;
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const {
    getReviews,
    getReviewById,
    updateStatus,
    updateInternalNote,
    generateAiSuggestion,
    postReply,
    deleteReply,
    getEntertainmentRatings,
    runFullSync,
    acquireReviewLock,
    releaseReviewLock,
  } = useGoogleReview();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [locationOptions, setLocationOptions] = useState([]);
  const [matchOpen, setMatchOpen] = useState(false);

  const [selectedId, setSelectedId] = useState(null);
  const [selectedReview, setSelectedReview] = useState(null);

  const [filters, setFilters] = useState({
    status: '',
    minStars: '',
    maxStars: '',
    googleLocationName: '',
    hasReply: '',
    search: '',
  });

  const [savingStatus, setSavingStatus] = useState(false);
  const [generatingAi, setGeneratingAi] = useState(false);
  const [postingReply, setPostingReply] = useState(false);
  const [deletingReply, setDeletingReply] = useState(false);
  const [savingNote, setSavingNote] = useState(false);

  // Coordination lock state for the currently selected review.
  // `locked` reflects whether any user holds the lock; `lockedByOtherUser`
  // additionally means "not us", which is what gates the edit controls.
  const [lockState, setLockState] = useState({
    locked: false,
    lockedBy: null,
    lockedByUsername: null,
    lockedByOtherUser: false,
  });
  const [leftPanelWidth, setLeftPanelWidth] = useState(36);
  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef(null);
  const listScrollRef = useRef(null);
  const resizeStartX = useRef(0);
  const resizeStartWidth = useRef(0);
  const loadSeqRef = useRef(0);

  // Use refs to avoid stale closures in the load function without triggering
  // unnecessary re-renders.
  const filtersRef = useRef(filters);
  const pageRef = useRef(page);
  const getReviewsRef = useRef(getReviews);
  const loadingRef = useRef(loading);
  const loadingMoreRef = useRef(loadingMore);
  const hasMoreRef = useRef(hasMore);
  const acquireReviewLockRef = useRef(acquireReviewLock);
  const releaseReviewLockRef = useRef(releaseReviewLock);
  useEffect(() => { filtersRef.current = filters; }, [filters]);
  useEffect(() => { pageRef.current = page; }, [page]);
  useEffect(() => { getReviewsRef.current = getReviews; }, [getReviews]);
  useEffect(() => { loadingRef.current = loading; }, [loading]);
  useEffect(() => { loadingMoreRef.current = loadingMore; }, [loadingMore]);
  useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);
  useEffect(() => { acquireReviewLockRef.current = acquireReviewLock; }, [acquireReviewLock]);
  useEffect(() => { releaseReviewLockRef.current = releaseReviewLock; }, [releaseReviewLock]);

  // Stable load trigger: incrementing this counter fires a data reload.
  const [loadTick, setLoadTick] = useState(0);
  const triggerLoad = useCallback(() => setLoadTick(t => t + 1), []);

  const loadReviewsPage = useCallback(async ({ pageToLoad = 1, append = false } = {}) => {
    if (!canRead) return;
    const requestSeq = ++loadSeqRef.current;
    const f = filtersRef.current;

    if (append) {
      loadingMoreRef.current = true;
      setLoadingMore(true);
    } else {
      loadingRef.current = true;
      loadingMoreRef.current = false;
      setLoading(true);
      setLoadingMore(false);
      setHasMore(false);
      hasMoreRef.current = false;
    }

    try {
      const data = await getReviewsRef.current({
        status: f.status || undefined,
        minStars: f.minStars || undefined,
        maxStars: f.maxStars || undefined,
        googleLocationName: f.googleLocationName || undefined,
        hasReply: f.hasReply === '' ? undefined : f.hasReply,
        search: f.search || undefined,
        page: pageToLoad,
        limit: PAGE_SIZE,
      });

      if (requestSeq !== loadSeqRef.current) return;

      const fetched = data?.items || [];
      const nextTotal = data?.total || 0;
      setItems(prev => {
        if (!append) return fetched;
        const existingIds = new Set(prev.map(r => r._id));
        return prev.concat(fetched.filter(r => !existingIds.has(r._id)));
      });
      setTotal(nextTotal);
      setPage(pageToLoad);
      setHasMore(pageToLoad * PAGE_SIZE < nextTotal);
      hasMoreRef.current = pageToLoad * PAGE_SIZE < nextTotal;

      if (!append) {
        if (fetched.length > 0 && isDesktop) {
          setSelectedId(prev => {
            const stillThere = fetched.find(r => r._id === prev);
            if (!stillThere) {
              setSelectedReview(fetched[0]);
              return fetched[0]._id;
            }
            return prev;
          });
        } else if (fetched.length === 0) {
          setSelectedId(null);
          setSelectedReview(null);
        }
        if (listScrollRef.current) {
          listScrollRef.current.scrollTop = 0;
        }
      }
    } catch (_e) {
    } finally {
      if (requestSeq === loadSeqRef.current) {
        if (append) {
          loadingMoreRef.current = false;
          setLoadingMore(false);
        } else {
          loadingRef.current = false;
          setLoading(false);
        }
      }
    }
  }, [canRead, isDesktop]);

  useEffect(() => {
    loadReviewsPage({ pageToLoad: 1, append: false });
  }, [filters, loadTick, loadReviewsPage]);

  // Build the "Angebot" filter options. Once an entertainment is assigned to a
  // Google location, the filter shows the entertainment name instead of the raw
  // Google location title; locations without a mapping fall back to the Google
  // title. Filtering still runs by googleLocationName so all reviews of a shared
  // venue are returned regardless of which entertainment owns the denormalised
  // mapping (a location can back more than one entertainment).
  const locationsLoadedRef = useRef(false);
  useEffect(() => {
    if (!canRead || locationsLoadedRef.current) return;
    locationsLoadedRef.current = true;
    getEntertainmentRatings()
      .then(data => {
        const items = Array.isArray(data?.items) ? data.items : [];
        const locs = Array.isArray(data?.locations) ? data.locations : [];
        const namesByLocation = new Map();
        items.forEach(it => {
          const resourceName = it.googleLocationName;
          const name = it.longName || it.entertainmentName || '';
          if (!resourceName || !name) return;
          const names = namesByLocation.get(resourceName) || [];
          if (!names.includes(name)) names.push(name);
          namesByLocation.set(resourceName, names);
        });
        const options = locs.map(loc => {
          const names = namesByLocation.get(loc.resourceName);
          return {
            value: loc.resourceName,
            label: names && names.length ? names.join(', ') : loc.title || loc.resourceName,
          };
        });
        options.sort((a, b) => a.label.localeCompare(b.label, 'de'));
        setLocationOptions(options);
      })
      .catch(() => {
        locationsLoadedRef.current = false;
      });
  }, [canRead, getEntertainmentRatings]);

  // Lock lifecycle: whenever the user opens a review, claim the edit lock,
  // renew it periodically while the detail pane stays open, and release it
  // when the user switches to another review or closes the pane. Failures
  // with status 423 mean the lock is held by someone else – we surface that
  // in the UI instead of treating it as an error.
  const reviewIdForLock = selectedReview?._id || null;
  useEffect(() => {
    if (!reviewIdForLock) {
      setLockState({ locked: false, lockedBy: null, lockedByUsername: null, lockedByOtherUser: false });
      return undefined;
    }
    let cancelled = false;

    const tryAcquire = async () => {
      try {
        const res = await acquireReviewLockRef.current(reviewIdForLock);
        if (cancelled) return;
        setLockState({
          locked: true,
          lockedBy: res?.lockedBy || null,
          lockedByUsername: null,
          lockedByOtherUser: false,
        });
      } catch (err) {
        if (cancelled) return;
        if (err?.statusCode === 423) {
          setLockState({
            locked: true,
            lockedBy: err?.data?.currentLock || null,
            lockedByUsername: err?.data?.lockedByUsername || null,
            lockedByOtherUser: true,
          });
        } else {
          setLockState({ locked: false, lockedBy: null, lockedByUsername: null, lockedByOtherUser: false });
        }
      }
    };

    tryAcquire();
    const interval = setInterval(tryAcquire, 2 * 60 * 1000);

    return () => {
      cancelled = true;
      clearInterval(interval);
      releaseReviewLockRef.current?.(reviewIdForLock).catch(() => {});
    };
  }, [reviewIdForLock]);

  const handleSelect = useCallback(
    async review => {
      setSelectedId(review._id);
      setSelectedReview(review);
      try {
        const full = await getReviewById(review._id);
        setSelectedReview(full);
      } catch (_e) {}
    },
    [getReviewById]
  );

  const updateSelectedInList = useCallback(next => {
    setSelectedReview(next);
    setItems(prev => prev.map(r => (r._id === next._id ? { ...r, ...next } : r)));
  }, []);

  // Maps a 423 LOCKED error from any mutating endpoint into the local lock
  // state so the UI immediately switches to read-only and shows the owner.
  const handleLockConflict = useCallback(err => {
    if (err?.statusCode !== 423) return false;
    const lockedByUsername = err?.data?.lockedByUsername || null;
    setLockState({
      locked: true,
      lockedBy: err?.data?.currentLock || null,
      lockedByUsername,
      lockedByOtherUser: true,
    });
    toast.error(
      `Rezension wird gerade von ${lockedByUsername || 'einer/einem anderen Benutzer:in'} bearbeitet`
    );
    return true;
  }, []);

  const handleStatusChange = useCallback(
    async status => {
      if (!selectedReview) return;
      setSavingStatus(true);
      try {
        await updateStatus({ reviewId: selectedReview._id, status });
        updateSelectedInList({ ...selectedReview, status });
      } catch (err) {
        handleLockConflict(err);
      } finally {
        setSavingStatus(false);
      }
    },
    [selectedReview, updateStatus, updateSelectedInList, handleLockConflict]
  );

  const handleGenerateAi = useCallback(async () => {
    if (!selectedReview) return null;
    setGeneratingAi(true);
    try {
      const resp = await generateAiSuggestion({ reviewId: selectedReview._id });
      updateSelectedInList({ ...selectedReview, aiSuggestion: resp.aiSuggestion });
      return resp;
    } catch (err) {
      handleLockConflict(err);
      return null;
    } finally {
      setGeneratingAi(false);
    }
  }, [selectedReview, generateAiSuggestion, updateSelectedInList, handleLockConflict]);

  const handlePostReply = useCallback(
    async comment => {
      if (!selectedReview) return;
      setPostingReply(true);
      try {
        const resp = await postReply({ reviewId: selectedReview._id, comment });
        updateSelectedInList({ ...selectedReview, reply: resp.reply, status: resp.status || 'resolved' });
        toast.success('Antwort wurde auf Google veröffentlicht');
      } catch (err) {
        handleLockConflict(err);
      } finally {
        setPostingReply(false);
      }
    },
    [selectedReview, postReply, updateSelectedInList, handleLockConflict]
  );

  const handleDeleteReply = useCallback(async () => {
    if (!selectedReview) return;
    setDeletingReply(true);
    try {
      await deleteReply({ reviewId: selectedReview._id });
      updateSelectedInList({ ...selectedReview, reply: { comment: '', updateTime: null }, status: 'inProgress' });
      toast.success('Antwort entfernt');
    } catch (err) {
      handleLockConflict(err);
    } finally {
      setDeletingReply(false);
    }
  }, [selectedReview, deleteReply, updateSelectedInList, handleLockConflict]);

  const handleSaveNote = useCallback(
    async internalNote => {
      if (!selectedReview) return;
      setSavingNote(true);
      try {
        await updateInternalNote({ reviewId: selectedReview._id, internalNote });
        updateSelectedInList({ ...selectedReview, internalNote });
        toast.success('Notiz gespeichert');
      } catch (err) {
        handleLockConflict(err);
      } finally {
        setSavingNote(false);
      }
    },
    [selectedReview, updateInternalNote, updateSelectedInList, handleLockConflict]
  );

  const handleSync = useCallback(async () => {
    setSyncing(true);
    try {
      const res = await runFullSync();
      toast.success(`Sync abgeschlossen (${res?.reviews?.upserted || 0} Rezensionen aktualisiert)`);
      setPage(1);
      triggerLoad();
    } catch (_e) {
    } finally {
      setSyncing(false);
    }
  }, [runFullSync, triggerLoad]);

  const setFilter = useCallback((key, value) => {
    setPage(1);
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleLoadMore = useCallback(() => {
    if (!hasMoreRef.current || loadingRef.current || loadingMoreRef.current) return;
    loadReviewsPage({ pageToLoad: pageRef.current + 1, append: true });
  }, [loadReviewsPage]);

  const handleListScroll = useCallback(
    e => {
      const el = e.currentTarget;
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 320) {
        handleLoadMore();
      }
    },
    [handleLoadMore]
  );

  useEffect(() => {
    const el = listScrollRef.current;
    if (!el || loading || loadingMore || !hasMore) return;
    if (el.scrollHeight <= el.clientHeight + 80) {
      handleLoadMore();
    }
  }, [items.length, loading, loadingMore, hasMore, handleLoadMore]);

  const handleResizeStart = e => {
    setIsResizing(true);
    resizeStartX.current = e.clientX;
    resizeStartWidth.current = leftPanelWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const handleResizeMove = useCallback(
    e => {
      if (!isResizing || !containerRef.current) return;

      const containerWidth = containerRef.current.offsetWidth;
      const deltaX = e.clientX - resizeStartX.current;
      const deltaPercentage = (deltaX / containerWidth) * 100;
      const minWidthPercentage = Math.max(25, (280 / containerWidth) * 100);
      const newWidth = Math.max(
        minWidthPercentage,
        Math.min(80, resizeStartWidth.current + deltaPercentage)
      );

      setLeftPanelWidth(newWidth);
    },
    [isResizing]
  );

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', handleResizeMove);
      window.addEventListener('mouseup', handleResizeEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleResizeMove);
      window.removeEventListener('mouseup', handleResizeEnd);
    };
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  if (!canRead) {
    return <Navigate to='/welcome' replace />;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 64px)',
        overflow: 'hidden',
        py: 2,
      }}
    >
      <Paper sx={{ p: { xs: 1.5, md: 2 }, mb: 2, overflow: 'hidden' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(5, minmax(140px, 1fr)) auto',
            },
            gap: 1.5,
            alignItems: 'center',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <TextField
              size='small'
              fullWidth
              placeholder='Rezensionen durchsuchen'
              defaultValue={filters.search}
              onChange={e => setFilter('search', e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon fontSize='small' sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <FormControl size='small' fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                label='Status'
                value={filters.status}
                onChange={e => setFilter('status', e.target.value)}
                MenuProps={{ disableScrollLock: true }}
              >
                <MenuItem value=''>Alle</MenuItem>
                <MenuItem value='new'>Neu</MenuItem>
                <MenuItem value='inProgress'>In Bearbeitung</MenuItem>
                <MenuItem value='resolved'>Beantwortet</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <FormControl size='small' fullWidth>
              <InputLabel>Sterne</InputLabel>
              <Select
                label='Sterne'
                value={
                  filters.minStars === 1 && filters.maxStars === 2
                    ? 'neg'
                    : filters.minStars === 3 && filters.maxStars === 3
                      ? 'mid'
                      : filters.minStars === 4 && filters.maxStars === 5
                        ? 'pos'
                        : ''
                }
                onChange={e => {
                  const v = e.target.value;
                  if (v === '') {
                    setFilters(prev => ({ ...prev, minStars: '', maxStars: '' }));
                    setPage(1);
                  } else if (v === 'neg') {
                    setFilters(prev => ({ ...prev, minStars: 1, maxStars: 2 }));
                    setPage(1);
                  } else if (v === 'mid') {
                    setFilters(prev => ({ ...prev, minStars: 3, maxStars: 3 }));
                    setPage(1);
                  } else if (v === 'pos') {
                    setFilters(prev => ({ ...prev, minStars: 4, maxStars: 5 }));
                    setPage(1);
                  }
                }}
                MenuProps={{ disableScrollLock: true }}
              >
                <MenuItem value=''>Alle</MenuItem>
                <MenuItem value='neg'>1-2 ★ (negativ)</MenuItem>
                <MenuItem value='mid'>3 ★ (neutral)</MenuItem>
                <MenuItem value='pos'>4-5 ★ (positiv)</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <FormControl size='small' fullWidth>
              <InputLabel>Angebot</InputLabel>
              <Select
                label='Angebot'
                value={filters.googleLocationName}
                onChange={e => setFilter('googleLocationName', e.target.value)}
                MenuProps={{ disableScrollLock: true }}
              >
                <MenuItem value=''>Alle</MenuItem>
                {locationOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <FormControl size='small' fullWidth>
              <InputLabel>Antwort</InputLabel>
              <Select
                label='Antwort'
                value={filters.hasReply}
                onChange={e => setFilter('hasReply', e.target.value)}
                MenuProps={{ disableScrollLock: true }}
              >
                <MenuItem value=''>Alle</MenuItem>
                <MenuItem value='false'>Ohne Antwort</MenuItem>
                <MenuItem value='true'>Mit Antwort</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: { xs: 'flex-end', sm: 'flex-start', md: 'flex-end' },
              gap: 0.5,
              gridColumn: { xs: '1 / -1', sm: 'auto', md: 'auto' },
            }}
          >
            <Tooltip title='Google-Standorte zuordnen'>
              <span>
                <IconButton onClick={() => setMatchOpen(true)}>
                  <StorefrontIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title='Sofort-Sync starten'>
              <span>
                <IconButton onClick={handleSync} disabled={syncing || loading}>
                  {syncing ? <CircularProgress size={20} /> : <SyncIcon />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title='Liste neu laden'>
              <span>
                <IconButton onClick={triggerLoad} disabled={loading}>
                  {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      <Box
        ref={containerRef}
        sx={{
          display: 'flex',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        <Box
          sx={{
            width: { xs: '100%', md: `${leftPanelWidth}%` },
            display: { xs: selectedReview ? 'none' : 'flex', md: 'flex' },
            flexDirection: 'column',
            transition: isResizing ? 'none' : 'width 0.3s ease',
            overflow: 'hidden',
            pr: { md: 1 },
            minHeight: 0,
          }}
        >
          <Box
            ref={listScrollRef}
            onScroll={handleListScroll}
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              pr: { md: 0.5 },
            }}
          >
            {loading ? (
              <Stack alignItems='center' sx={{ p: 4 }}>
                <CircularProgress size={24} />
              </Stack>
            ) : items.length === 0 ? (
              <Paper sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                <Typography variant='body2'>Keine Rezensionen gefunden.</Typography>
              </Paper>
            ) : (
              <>
                {items.map(r => (
                  <ReviewCard
                    key={r._id}
                    review={r}
                    selected={r._id === selectedId}
                    onSelect={handleSelect}
                  />
                ))}
                {loadingMore ? (
                  <Stack alignItems='center' sx={{ py: 2 }}>
                    <CircularProgress size={20} />
                  </Stack>
                ) : null}
              </>
            )}
          </Box>

          <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
            useFlexGap
            sx={{ mt: 1, gap: 1, flexWrap: 'wrap' }}
          >
            <Typography variant='caption' color='text.secondary' sx={{ flexShrink: 0 }}>
              {total > 0
                ? `${items.length} von ${total} Rezensionen geladen`
                : '0 Rezensionen'}
            </Typography>
            {hasMore ? (
              <Typography variant='caption' color='text.secondary'>
                Zum Nachladen weiter scrollen
              </Typography>
            ) : null}
          </Stack>
        </Box>

        <Box
          sx={{
            display: { xs: 'none', md: 'block' },
            width: '4px',
            cursor: 'col-resize',
            bgcolor: 'transparent',
            flexShrink: 0,
            position: 'relative',
            '&:hover': {
              bgcolor: 'primary.main',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: '-2px',
              width: '8px',
              height: '100%',
              cursor: 'col-resize',
            },
          }}
          onMouseDown={handleResizeStart}
        />

        <Box
          sx={{
            flex: 1,
            display: { xs: selectedReview ? 'block' : 'none', md: 'block' },
            overflow: 'auto',
            pl: { md: 2 },
            transition: isResizing ? 'none' : 'width 0.3s ease',
          }}
        >
          {selectedReview ? (
            <Button
              size='small'
              onClick={() => { setSelectedId(null); setSelectedReview(null); }}
              sx={{ display: { xs: 'inline-flex', md: 'none' }, mb: 1 }}
            >
              ← Zurück zur Liste
            </Button>
          ) : null}
          <ReviewDetail
            review={selectedReview}
            onStatusChange={handleStatusChange}
            onGenerateAi={handleGenerateAi}
            onPostReply={handlePostReply}
            onDeleteReply={handleDeleteReply}
            onSaveNote={handleSaveNote}
            savingStatus={savingStatus}
            generatingAi={generatingAi}
            postingReply={postingReply}
            deletingReply={deletingReply}
            savingNote={savingNote}
            lockState={lockState}
          />
        </Box>
      </Box>

      <GoogleRatingMatchDialog
        open={matchOpen}
        onClose={() => setMatchOpen(false)}
        onConfirmed={() => triggerLoad()}
      />
    </Box>
  );
}
